"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { ArrowUpRight } from "@phosphor-icons/react";

/** public 路径（文件夹含空格，URL 编码） */
const IMAGE_BASE = "/image/new-version/molding/plastic-injection-molding/success-stories";

const SLIDE_COUNT = 4;

/** 与 en.json → plasticInjectionMoldingSuccessStories 中 key 一一对应 */
const SLIDE_I18N_KEYS = [
	{ caseTitle: "slide1_caseTitle", desc: "slide1_desc" },
	{ caseTitle: "slide2_caseTitle", desc: "slide2_desc" },
	{ caseTitle: "slide3_caseTitle", desc: "slide3_desc" },
	{ caseTitle: "slide4_caseTitle", desc: "slide4_desc" },
] as const;

const CASE_STUDY_HREF = "/cases";
/** 自动轮播间隔（毫秒） */
const AUTOPLAY_MS = 6000;

/**
 * Plastic Injection Molding 页底部：成功案例（左文右图，50/50；圆点切换时文案同步）
 */
export default function PlasticInjectionMoldingSuccessStories() {
	const t = useTranslations("plasticInjectionMoldingSuccessStories");
	const [active, setActive] = useState(0);

	const { caseTitle: caseTitleKey, desc: descKey } = SLIDE_I18N_KEYS[active];

	useEffect(() => {
		const id = window.setInterval(() => {
			setActive((i) => (i + 1) % SLIDE_COUNT);
		}, AUTOPLAY_MS);
		return () => window.clearInterval(id);
	}, []);

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/*
				 * 标题：整块居中；两行左缘对齐（grid w-max）；绿线与第一行文字底缘对齐
				 */}
				<h2 className="mx-auto mb-6 grid w-max max-w-full grid-cols-1 gap-y-1 text-left sm:mb-8 md:mb-10 lg:mb-12">
					<span className="flex min-w-0 items-end gap-2 sm:gap-2.5">
						<span className="mr-8 shrink-0 text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							{t("titlePart1")}
						</span>
						<span className="h-0.5 min-h-[2px] flex-1 origin-left rounded-full bg-[#76a734]" aria-hidden />
					</span>
					<span className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("titlePart2")}
					</span>
				</h2>

				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-0">
					{/* 左：标题 / 引号 / 正文 / 链接同一左内边距，全部左对齐 */}
					<div className="flex w-full min-w-0 flex-col justify-center self-stretch px-0 py-2 text-left lg:w-3/5 lg:pr-8 xl:pr-12">
						<div className="pl-6 sm:pl-8 md:pl-10">
							{/* 仅包裹标题，供引号 top:100% 相对标题底边定位 */}
							<div className="relative inline-block w-full max-w-full">
								<p className="mb-0 text-base font-medium italic leading-snug text-gray-900 md:text-3xl">
									{t(caseTitleKey)}
								</p>
								{/* 开引号：与标题/正文左缘对齐（left-0），不占文档流 */}
								<span
									className="pointer-events-none absolute left-0 top-[calc(100%+0.25rem)] font-serif text-[3.25rem] leading-[0.92] text-[#76a734] sm:text-[3.75rem] md:top-[calc(100%+0.375rem)] md:text-[4.25rem] lg:text-[4.75rem]"
									aria-hidden
								>
									&ldquo;
								</span>
							</div>
							<p className="mb-8 mt-[2.75rem] text-sm leading-relaxed text-gray-900 sm:mt-[3rem] md:mt-[3.25rem] md:text-xl">
								{t(descKey)}
							</p>
							<a
								href={CASE_STUDY_HREF}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-[#76a734] transition-opacity hover:opacity-80 sm:text-base"
							>
								{t("readCaseStudy")}
								<ArrowUpRight className="h-5 w-5" weight="bold" aria-hidden />
							</a>
						</div>
					</div>

					{/* 右：轮播图 + 图片正下方四个圆点切换 */}
					<div className="flex w-full min-w-0 flex-col lg:w-2/5">
						<div className="relative aspect-[4/2] w-full overflow-hidden rounded-md bg-gray-100 lg:aspect-[5/4]">
							{SLIDE_I18N_KEYS.map((_, i) => (
								<div
									key={i}
									className={`absolute inset-0 transition-opacity duration-500 ${
										active === i ? "z-10 opacity-100" : "z-0 opacity-0"
									}`}
									aria-hidden={active !== i}
								>
									<Image
										src={`${IMAGE_BASE}/${i + 1}.webp`}
										alt={t(SLIDE_I18N_KEYS[i].caseTitle)}
										fill
										className="object-cover"
										sizes="(max-width: 1024px) 100vw, 50vw"
										priority={i === 0}
									/>
								</div>
							))}
						</div>
						{/* 始终在图片容器下方水平居中；四个圆点可点击切换 */}
						<div
							className="mt-4 flex shrink-0 justify-center gap-2.5"
							role="tablist"
							aria-label={t("carouselDotsLabel")}
						>
							{SLIDE_I18N_KEYS.map((_, i) => (
								<button
									key={i}
									type="button"
									role="tab"
									aria-selected={active === i}
									aria-label={t("goToSlide", { n: i + 1 })}
									onClick={() => setActive(i)}
									className="inline-flex min-h-[30px] min-w-[30px] items-center justify-center rounded-full"
								>
									<span
										className={`block h-2.5 w-2.5 rounded-full transition-colors ${
											active === i ? "bg-[#76a734]" : "bg-gray-300 hover:bg-gray-400"
										}`}
									/>
								</button>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
